/* Brand shell behaviour. Single authoritative header controller.

   THE SCROLL CONTAINER IS NOT THE WINDOW ON DESKTOP.
   Horizon's assets/base.css contains:

     @media (min-width: 990px) {
       html:has(.page-wrapper), html:has(.page-wrapper) body { height:100dvh; overflow:hidden; }
       .page-wrapper { height:100dvh; overflow-y:auto; }
     }

   so at >=990px the document does not scroll - .page-wrapper does. A listener on
   window therefore never fires and window.scrollY is permanently 0, which is why an
   earlier version of this file left the header permanently visible on the live
   storefront while appearing to work in the Theme Editor (whose preview frame is
   usually narrower than 990px, where the document is still the scroller).

   Horizon solves this in assets/scroll-container.js via getScrollEventTarget().
   scrollTarget() below mirrors that logic deliberately - if Horizon changes its
   breakpoint, this must change with it.

   The header is position:fixed (see assets/ref-brand-header.css for why sticky cannot
   work in this markup). This file owns every header behaviour: the solid/transparent
   state, the hide-on-down / reveal-on-up transform, and the --bx-header-top offset that
   keeps the header under the announcement bar until that bar has scrolled away. There
   is deliberately no second controller in the section markup. */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var SQUEEZE = window.matchMedia('(min-width: 990px)');

  /* Mirrors getScrollEventTarget() in assets/scroll-container.js. */
  function scrollTarget() {
    if (SQUEEZE.matches) return document.querySelector('.page-wrapper') || document;
    return document;
  }

  function scrollPos(target) {
    if (target && target !== document && typeof target.scrollTop === 'number') return target.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  function initHeader(header) {
    if (!header || header.dataset.bxInit === '1') return;
    header.dataset.bxInit = '1';

    var section = header.closest('.bx-header-section') || document;
    var topbar = section.querySelector ? section.querySelector('.bx-topbar') : null;

    var mode = header.dataset.sticky || 'scroll-up';
    var over = header.classList.contains('bx-header--over');
    if (over) document.body.classList.add('bx-has-over-header');

    var target = scrollTarget();
    var lastY = scrollPos(target);
    var ticking = false;
    var barH = topbar ? topbar.offsetHeight : 0;
    var lastTop = -1;

    var SOLID_AT = 24;    // px scrolled before the transparent state resolves
    var FREE_ZONE = 220;  // never hide the bar inside this band
    var DELTA = 6;        // ignore sub-pixel / trackpad jitter

    function menuOpen() {
      return !!header.querySelector('.bx-nav__item.is-open') ||
             document.body.classList.contains('bx-drawer-open');
    }

    /* Keeps the fixed header sitting under the announcement bar until that bar has
       scrolled away, then pins it to the viewport top. Measured, so a store with no
       announcement bar gets 0 and the header pins immediately. */
    function writeOffset(y) {
      var top = Math.max(0, barH - y);
      if (top === lastTop) return;
      lastTop = top;
      header.style.setProperty('--bx-header-top', top + 'px');
    }

    function apply() {
      ticking = false;
      var y = scrollPos(target);
      var delta = y - lastY;

      writeOffset(y);

      if (over) header.classList.toggle('is-stuck', y > SOLID_AT);

      if (mode === 'none') {
        header.classList.remove('is-hidden');
        lastY = y;
        return;
      }

      header.classList.toggle('is-menu-open', menuOpen());

      if (mode === 'always' || menuOpen()) {
        header.classList.remove('is-hidden');
        lastY = y;
        return;
      }

      if (Math.abs(delta) > DELTA) {
        if (y <= FREE_ZONE) {
          header.classList.remove('is-hidden');
        } else if (delta > 0) {
          header.classList.add('is-hidden');
        } else {
          header.classList.remove('is-hidden');
        }
        lastY = y;
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    function bind(t) {
      t.addEventListener('scroll', onScroll, { passive: true });
    }
    function unbind(t) {
      t.removeEventListener('scroll', onScroll);
    }

    bind(target);

    /* The scroll container swaps at the 990px breakpoint, so the listener has to move
       with it or the header freezes on the far side of a resize. */
    function retarget() {
      var next = scrollTarget();
      if (next === target) return;
      unbind(target);
      target = next;
      bind(target);
      lastY = scrollPos(target);
      lastTop = -1;
      apply();
    }

    function remeasure() {
      barH = topbar ? topbar.offsetHeight : 0;
      lastTop = -1;
      retarget();
      apply();
    }

    if (SQUEEZE.addEventListener) SQUEEZE.addEventListener('change', remeasure);
    else if (SQUEEZE.addListener) SQUEEZE.addListener(remeasure);
    window.addEventListener('resize', remeasure);
    if (window.ResizeObserver && topbar) new ResizeObserver(remeasure).observe(topbar);

    apply();

    /* Dropdown panels: hover on fine pointers, click and keyboard everywhere. */
    var items = Array.prototype.slice.call(header.querySelectorAll('.bx-nav__item.has-panel'));
    var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    function closeAll(except) {
      items.forEach(function (it) {
        if (it === except) return;
        it.classList.remove('is-open');
        var l = it.querySelector('.bx-nav__link');
        if (l) l.setAttribute('aria-expanded', 'false');
      });
      header.classList.toggle('is-menu-open', menuOpen());
    }

    items.forEach(function (item) {
      var link = item.querySelector('.bx-nav__link');
      function open() {
        closeAll(item);
        item.classList.add('is-open');
        header.classList.remove('is-hidden');
        header.classList.add('is-menu-open');
        if (link) link.setAttribute('aria-expanded', 'true');
      }
      function close() {
        item.classList.remove('is-open');
        if (link) link.setAttribute('aria-expanded', 'false');
        header.classList.toggle('is-menu-open', menuOpen());
      }
      if (fine) {
        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', close);
        item.addEventListener('focusin', open);
      } else if (link) {
        link.addEventListener('click', function (e) {
          if (!item.classList.contains('is-open')) { e.preventDefault(); open(); }
        });
      }
    });

    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(null); });
    document.addEventListener('click', function (e) { if (!header.contains(e.target)) closeAll(null); });
  }

  function initDrawer(scope) {
    var drawer = (scope || document).querySelector('[data-bx-drawer]');
    if (!drawer || drawer.dataset.bxInit === '1') return;
    drawer.dataset.bxInit = '1';

    var openers = document.querySelectorAll('[data-bx-drawer-open]');
    var closers = drawer.querySelectorAll('[data-bx-drawer-close]');
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.hidden = false;
      window.requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      document.body.classList.add('bx-drawer-open');
      document.body.style.overflow = 'hidden';
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      var first = drawer.querySelector('a, button');
      if (first) first.focus({ preventScroll: true });
    }
    function close() {
      drawer.classList.remove('is-open');
      document.body.classList.remove('bx-drawer-open');
      document.body.style.overflow = '';
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      window.setTimeout(function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, reduce ? 0 : 350);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    openers.forEach(function (b) { b.addEventListener('click', open); });
    closers.forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  function boot(scope) {
    var s = scope || document;
    s.querySelectorAll('.bx-header').forEach(initHeader);
    initDrawer(s);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
